import React, { Suspense, useEffect, useCallback } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useProfile } from "@/hooks/useProfile";
import { useSessionTimeout } from "@/hooks/useSessionTimeout";
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";
import { FeatureFlagsProvider } from "@/hooks/useFeatureFlags";
import { ProfileProvider } from "@/hooks/useProfile";
import { OfflineProvider } from "@/hooks/useOffline";
import { GroupProvider } from "@/components/GroupSwitcher";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import StateLoading from "@/components/StateLoading";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

async function recordSignOut(userId: string) {
  try {
    await supabase.from("login_activity").insert({
      user_id: userId,
      event_type: "sign_out",
      user_agent: navigator.userAgent,
      login_method: null,
    } as any);
  } catch { /* silently fail */ }
}

const Auth = React.lazy(() => import("./pages/Auth"));
const AppLayout = React.lazy(() => import("./components/AppLayout"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const GroupPage = React.lazy(() => import("./pages/GroupPage"));
const LedgerPage = React.lazy(() => import("./pages/LedgerPage"));
const LoansPage = React.lazy(() => import("./pages/LoansPage"));
const MeetingsPage = React.lazy(() => import("./pages/MeetingsPage"));
const ReportsPage = React.lazy(() => import("./pages/ReportsPage"));
const HelpPage = React.lazy(() => import("./pages/HelpPage"));
const SupportPage = React.lazy(() => import("./pages/SupportPage"));
const RTMPage = React.lazy(() => import("./pages/admin/RTMPage"));
const PromptLogPage = React.lazy(() => import("./pages/admin/PromptLogPage"));
const AuditLogPage = React.lazy(() => import("./pages/admin/AuditLogPage"));
const DemoModePage = React.lazy(() => import("./pages/admin/DemoModePage"));
const DemoUsersPage = React.lazy(() => import("./pages/admin/DemoUsersPage"));
const PreDemoChecklistPage = React.lazy(() => import("./pages/admin/PreDemoChecklistPage"));
const AccountSettingsPage = React.lazy(() => import("./pages/AccountSettingsPage"));
const LateCasesPage = React.lazy(() => import("./pages/LateCasesPage"));
const CorrectionsPage = React.lazy(() => import("./pages/CorrectionsPage"));
const GoalsPage = React.lazy(() => import("./pages/GoalsPage"));
const AccountingPage = React.lazy(() => import("./pages/AccountingPage"));
const TicketsPage = React.lazy(() => import("./pages/TicketsPage"));
const ReconciliationPage = React.lazy(() => import("./pages/ReconciliationPage"));
const CompliancePage = React.lazy(() => import("./pages/CompliancePage"));
const DemoFeedbackPage = React.lazy(() => import("./pages/admin/DemoFeedbackPage"));
const DemoFeedbackCreatePage = React.lazy(() => import("./pages/admin/DemoFeedbackCreatePage"));
const DemoFeedbackDetailPage = React.lazy(() => import("./pages/admin/DemoFeedbackDetailPage"));
const ResetPasswordPage = React.lazy(() => import("./pages/ResetPasswordPage"));
const IntegrationsOutboxPage = React.lazy(() => import("./pages/admin/IntegrationsOutboxPage"));
const TrainingPage = React.lazy(() => import("./pages/TrainingPage"));
const DemoWalkthroughPage = React.lazy(() => import("./pages/admin/DemoWalkthroughPage"));
const ReimbursementsPage = React.lazy(() => import("./pages/ReimbursementsPage"));
const DeletionRequestsPage = React.lazy(() => import("./pages/admin/DeletionRequestsPage"));
const MessagesPage = React.lazy(() => import("./pages/MessagesPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const WelcomePage = React.lazy(() => import("./pages/WelcomePage"));

const queryClient = new QueryClient();

const ProtectedRoutes = React.forwardRef<HTMLDivElement>(function ProtectedRoutes(_props, _ref) {
  const { user, loading, signOut } = useAuth();
  const { showWarning, secondsLeft, stayLoggedIn } = useSessionTimeout(!!user);

  const handleSessionTimeout = useCallback(async () => {
    if (user) await recordSignOut(user.id);
    signOut();
    toast.info("You were logged out due to inactivity.");
  }, [signOut, user]);

  useEffect(() => {
    window.addEventListener("session-timeout", handleSessionTimeout);
    return () => window.removeEventListener("session-timeout", handleSessionTimeout);
  }, [handleSessionTimeout]);

  if (loading) return (
    <div className="flex min-h-screen items-center justify-center p-6">
      <StateLoading variant="page" />
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  return (
    <ProfileProvider>
      <FeatureFlagsProvider>
        <OfflineProvider>
          <GroupProvider>
            <SessionTimeoutWarning
              open={showWarning}
              secondsLeft={secondsLeft}
              onStayLoggedIn={stayLoggedIn}
              onLogout={async () => {
                if (user) await recordSignOut(user.id);
                signOut();
                toast.info("You have been logged out.");
              }}
            />
            <AppLayout />
          </GroupProvider>
        </OfflineProvider>
      </FeatureFlagsProvider>
    </ProfileProvider>
  );
});

const AdminRoutes = React.forwardRef<HTMLDivElement>(function AdminRoutes(_props, _ref) {
  const { primaryRole, loading } = useProfile();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <StateLoading variant="page" />
      </div>
    );
  }

  if (primaryRole !== "app_admin") {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
});

const DEMO_MODE = import.meta.env.VITE_DEMO_MODE === "true";
const DemoOnlyRoutes = React.forwardRef<HTMLDivElement>(function DemoOnlyRoutes(_props, _ref) {
  if (!DEMO_MODE) return <Navigate to="/" replace />;
  return <Outlet />;
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <ErrorBoundary>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<div className="flex min-h-screen items-center justify-center p-6"><StateLoading variant="page" /></div>}>
            <Routes>
              <Route path="/welcome" element={<WelcomePage />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route element={<ProtectedRoutes />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/group" element={<GroupPage />} />
                <Route path="/ledger" element={<LedgerPage />} />
                <Route path="/loans" element={<LoansPage />} />
                <Route path="/meetings" element={<MeetingsPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/help" element={<HelpPage />} />
                <Route path="/support" element={<SupportPage />} />
                <Route path="/settings" element={<AccountSettingsPage />} />
                <Route path="/late-cases" element={<LateCasesPage />} />
                <Route path="/corrections" element={<CorrectionsPage />} />
                <Route path="/goals" element={<GoalsPage />} />
                <Route path="/accounting" element={<AccountingPage />} />
                <Route path="/tickets" element={<TicketsPage />} />
                <Route path="/reconciliation" element={<ReconciliationPage />} />
                <Route path="/compliance" element={<CompliancePage />} />
                <Route path="/training" element={<TrainingPage />} />
                <Route path="/reimbursements" element={<ReimbursementsPage />} />
                <Route path="/messages" element={<MessagesPage />} />
                <Route element={<AdminRoutes />}>
                  <Route path="/admin/rtm" element={<RTMPage />} />
                  <Route path="/admin/audit-log" element={<AuditLogPage />} />
                  <Route path="/admin/integrations-outbox" element={<IntegrationsOutboxPage />} />
                  <Route path="/admin/deletion-requests" element={<DeletionRequestsPage />} />
                  <Route path="/admin/demo" element={<DemoModePage />} />
                  <Route path="/admin/prompt-log" element={<PromptLogPage />} />
                  <Route path="/admin/demo-users" element={<DemoUsersPage />} />
                  <Route path="/admin/pre-demo-checklist" element={<PreDemoChecklistPage />} />
                  <Route path="/admin/demo-feedback" element={<DemoFeedbackPage />} />
                  <Route path="/admin/demo-feedback/create" element={<DemoFeedbackCreatePage />} />
                  <Route path="/admin/demo-feedback/:id" element={<DemoFeedbackDetailPage />} />
                  <Route path="/admin/demo-walkthrough" element={<DemoWalkthroughPage />} />
                </Route>
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </ErrorBoundary>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
